import { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/utils';
import { CalendarDays, Activity } from 'lucide-react';
import { resolveAttributeValueForWeek } from '../../utils/periodizationPeriodBuckets';
import { getCellBackgroundColor } from '../../utils/periodizationValueSchema';
import AttributeValueBadge from './AttributeValueBadge';
import { acwrToHeatmapColor } from '../../utils/acwrHeatmapColor';

const PHASE_COLORS = {
  base: 'bg-emerald-500/80',
  build: 'bg-primary/80',
  peak: 'bg-rose-500/80',
  taper: 'bg-sky-500/80',
  recovery: 'bg-muted',
  transition: 'bg-violet-500/70',
  custom: 'bg-secondary',
};

function attrLabel(t, typeKey, fallback) {
  const k = `periodization.attributes.${typeKey}`;
  const tr = t(k);
  return tr !== k ? tr : fallback || typeKey;
}

function WeekBadge({ micro, phase, onWeekClick, onSessionsClick, draggable, onDragStart, onDragEnd }) {
  const { t } = useLanguage();
  const count = micro.planned_session_count ?? micro.linked_session_count ?? 0;
  const maxAcwr = micro.max_week_acwr;

  return (
    <div
      className={cn(
        'group relative flex items-center gap-0.5 rounded border border-border bg-muted/40',
        micro.is_deload && 'ring-1 ring-amber-400',
        draggable && 'cursor-grab active:cursor-grabbing'
      )}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <button
        type='button'
        onClick={() => onWeekClick?.(micro, phase)}
        className='rounded-l px-2 py-1 text-[10px] hover:bg-accent/60 transition-colors'
        title={`${micro.week_start} – ${micro.week_end}`}
      >
        W{micro.week_number_in_block}
        {micro.is_deload ? ` · ${t('periodization.deload')}` : ''}
      </button>
      <button
        type='button'
        onClick={(e) => {
          e.stopPropagation();
          onSessionsClick?.(micro, phase);
        }}
        className={cn(
          'flex items-center gap-0.5 border-l border-border px-1.5 py-1 text-[10px]',
          'hover:bg-accent/60 transition-colors',
          count > 0 ? 'text-foreground' : 'text-muted-foreground',
          !(maxAcwr != null && Number.isFinite(Number(maxAcwr))) && 'rounded-r'
        )}
        title={t('periodization.week.viewSessions', { count })}
      >
        <CalendarDays className='h-3 w-3' />
        <span className='tabular-nums'>{count}</span>
      </button>
      {maxAcwr != null && Number.isFinite(Number(maxAcwr)) && (
        <span
          className='flex items-center gap-0.5 border-l border-border px-1.5 py-1 text-[9px] font-semibold tabular-nums text-white rounded-r'
          style={{ backgroundColor: acwrToHeatmapColor(Number(maxAcwr)) }}
          title={t('periodization.week.maxAcwrTooltip', { value: Number(maxAcwr).toFixed(2) })}
        >
          <Activity className='h-2.5 w-2.5' />
          {Number(maxAcwr).toFixed(2)}
        </span>
      )}
    </div>
  );
}

export default function PeriodizationMatrix({
  tree,
  mode = 'matrix',
  onWeekClick,
  onSessionsClick,
  onWeekMove,
  draggableWeeks = false,
}) {
  const { t } = useLanguage();
  const [dragMicroId, setDragMicroId] = useState(null);
  const [dropPhaseId, setDropPhaseId] = useState(null);

  if (!tree?.phases?.length) {
    return (
      <p className='text-sm text-muted-foreground p-4'>{t('periodization.emptyBlock')}</p>
    );
  }

  const types = (tree.assessment_set?.attribute_types || []).filter(
    (type) => type.period_buckets?.week || type.period_buckets?.phase
  );
  const micros = tree.phases.flatMap((p) =>
    (p.microcycles || []).map((m) => ({ ...m, phase: p }))
  );

  const handleDrop = (phase) => {
    if (dragMicroId && onWeekMove) {
      onWeekMove(dragMicroId, phase.id);
    }
    setDragMicroId(null);
    setDropPhaseId(null);
  };

  if (mode === 'timeline') {
    return (
      <div className='space-y-2 p-2'>
        {draggableWeeks && (
          <p className='text-[10px] text-muted-foreground px-1'>{t('periodization.week.dragHint')}</p>
        )}
        {tree.phases.map((phase) => (
          <div
            key={phase.id}
            className={cn(
              'rounded-lg border border-border overflow-hidden transition-colors',
              dropPhaseId === phase.id && 'ring-2 ring-primary'
            )}
            onDragOver={(e) => {
              if (!draggableWeeks || !dragMicroId) return;
              e.preventDefault();
              setDropPhaseId(phase.id);
            }}
            onDragLeave={() => setDropPhaseId((id) => (id === phase.id ? null : id))}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(phase);
            }}
          >
            <div
              className={cn(
                'px-3 py-2 text-xs font-semibold text-primary-foreground flex justify-between',
                PHASE_COLORS[phase.phase_kind] || PHASE_COLORS.custom
              )}
            >
              <span>{phase.name}</span>
              <span className='font-normal opacity-90'>
                {phase.start_date} → {phase.end_date}
              </span>
            </div>
            <div className='flex flex-wrap gap-1 p-2 bg-card'>
              {(phase.microcycles || []).map((m) => (
                <WeekBadge
                  key={m.id}
                  micro={m}
                  phase={phase}
                  onWeekClick={onWeekClick}
                  onSessionsClick={onSessionsClick}
                  draggable={draggableWeeks}
                  onDragStart={(e) => {
                    setDragMicroId(m.id);
                    e.dataTransfer.setData('text/plain', String(m.id));
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={() => {
                    setDragMicroId(null);
                    setDropPhaseId(null);
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!types.length || !micros.length) {
    return <p className='text-sm text-muted-foreground p-4'>{t('periodization.noMatrixData')}</p>;
  }

  return (
    <div className='overflow-x-auto p-2'>
      <table className='w-full min-w-[480px] text-xs border-collapse'>
        <thead>
          <tr className='border-b border-border'>
            <th className='text-left p-2 font-medium text-muted-foreground sticky left-0 bg-card z-[1]'>
              {t('periodization.attribute')}
            </th>
            {micros.map((m) => (
              <th key={m.id} className='p-2 font-medium text-center min-w-[52px]'>
                <button
                  type='button'
                  className='hover:text-primary transition-colors'
                  onClick={() => onWeekClick?.(m, m.phase)}
                >
                  <div>W{m.week_number_in_block}</div>
                </button>
                <div className={cn('h-1 rounded mt-1', PHASE_COLORS[m.phase.phase_kind] || PHASE_COLORS.custom)} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {types.map((type) => (
            <tr key={type.type_key} className='border-b border-border/60'>
              <td className='p-2 sticky left-0 bg-card z-[1] font-medium'>
                {attrLabel(t, type.type_key, type.name)}
              </td>
              {micros.map((m) => {
                const resolved = resolveAttributeValueForWeek({
                  typeKey: type.type_key,
                  enabledBuckets: type.period_buckets,
                  block: tree?.block,
                  phase: m.phase,
                  micro: m,
                });
                const bg = getCellBackgroundColor(resolved.value, type);
                return (
                  <td
                    key={`${m.id}-${type.type_key}`}
                    className='p-2 text-center'
                    style={bg ? { backgroundColor: bg } : undefined}
                  >
                    <AttributeValueBadge value={resolved.value} type={type} compact />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}